/* Root application — routing for the CSP HR Portal with auth guards.
 *
 * Route-level code splitting via React.lazy — every page ships as its
 * own bundle chunk so the login-hot path stops pulling all 24 pages
 * (recharts, dompurify transitives, ~2-3 MB gzipped)
 * on first paint. Suspense fallback mirrors the ProtectedRoute
 * "Loading..." placeholder so a chunk load looks like an auth check
 * to the user — no new UX affordance.
 * (perf audit 2026-07-31 HIGH — bundle-size / initial-load)
 */
import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastHost } from "./components/Toast";

const LoginPage = lazy(() => import("./pages/LoginPage"));
// Unlisted on purpose: a diagnostic surface over live applicant data
// during the shadow-mode pilot, reached by URL and gated on the API.
const CIShadowPage = lazy(() => import("./pages/CIShadowPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const JobsPage = lazy(() => import("./pages/JobsPage"));
const JobDetailPage = lazy(() => import("./pages/JobDetailPage"));
const JobFormPage = lazy(() => import("./pages/JobFormPage"));
const CandidatesPage = lazy(() => import("./pages/CandidatesPage"));
const CandidateDetailPage = lazy(() => import("./pages/CandidateDetailPage"));
const SurveysPage = lazy(() => import("./pages/SurveysPage"));
const SurveyFormPage = lazy(() => import("./pages/SurveyFormPage"));
const ScrapersPage = lazy(() => import("./pages/ScrapersPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const CISurveyPage = lazy(() => import("./pages/CISurveyPage"));
const AptitudePage = lazy(() => import("./pages/AptitudePage"));
const SharedServicesPage = lazy(() => import("./pages/SharedServicesPage"));
const SurveyCompletionDashboardPage = lazy(() => import("./pages/SurveyCompletionDashboardPage"));
const StatsIndexPage = lazy(() => import("./pages/StatsIndexPage"));
const EmailOpenRatesPage = lazy(() => import("./pages/EmailOpenRatesPage"));
const EmailClicksPage = lazy(() => import("./pages/EmailClicksPage"));
const ReportsListPage = lazy(() => import("./pages/ReportsListPage"));
const DisqualifyListPage = lazy(() => import("./pages/DisqualifyListPage"));
const AptShadowPage = lazy(() => import("./pages/AptShadowPage"));
/* People who sat the assessments off the shared links and never
   applied. Not candidates, so deliberately not folded into
   /candidates — see the page header. */
const RecommendationsPage = lazy(() => import("./pages/RecommendationsPage"));
/* Prototypes of a rebuilt aptitude test, for discussion. Self-contained
   under pages/assessmentPreview/ so the whole thing comes out in one
   delete once the decision is made. */
const AssessmentPreviewIndex = lazy(() => import("./pages/assessmentPreview/Index"));
const AssessmentPreviewTimed = lazy(() => import("./pages/assessmentPreview/Timed"));
const AssessmentPreviewUntimed = lazy(() => import("./pages/assessmentPreview/Untimed"));
const ReportFormPage = lazy(() => import("./pages/ReportFormPage"));
const ReportViewPage = lazy(() => import("./pages/ReportViewPage"));
const PipelinePage = lazy(() => import("./pages/PipelinePage"));
const TeamMetricsPage = lazy(() => import("./pages/TeamMetricsPage"));
// In-house CI project — isolated parallel-flow lab. Route not linked
// from the nav; developer-only gate. See docs/BUILD-inhouse-assessments.md.
const CiLabPage = lazy(() => import("./pages/internal/CiLabPage"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const SignaturesPage = lazy(() => import("./pages/SignaturesPage"));
const SignPage = lazy(() => import("./pages/SignPage"));

const ALL_ROLES = ["admin", "hr_manager", "internal_hr", "internal_manager", "external_recruiter"];

const RouteLoading = () => (
  <div className="min-h-screen flex items-center justify-center">
    <p className="text-gray-500">Loading...</p>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              {/* Public */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/survey/:token" element={<SurveyFormPage />} />
              <Route path="/ci" element={<CISurveyPage />} />
              <Route path="/aptitude" element={<AptitudePage />} />
              {/* Public on purpose: the people who need to look at these do
                  not all have portal accounts. Nothing is stored, every item
                  is generated in the browser, and none of the live item bank
                  is reachable from here. The paths deliberately do NOT begin
                  with /aptitude — `is_our_game_link` matches that prefix and
                  would start minting assessment tokens for anyone who opened
                  one. */}
              <Route path="/assessment-preview" element={<AssessmentPreviewIndex />} />
              <Route path="/assessment-preview/timed" element={<AssessmentPreviewTimed />} />
              <Route path="/assessment-preview/untimed" element={<AssessmentPreviewUntimed />} />
              {/* Public sign page — no auth, token is the auth surface */}
              <Route path="/sign/:token" element={<SignPage />} />
              {/* Public open sign — one link per doc, anyone can sign
                  (Sean's whatsapp-share pivot 2026-08-05). Slug is
                  the doc's public slug; each submit creates a fresh
                  signature_request + capture server-side. */}
              <Route path="/sign-open/:slug" element={<SignPage />} />

              {/* Protected */}
              <Route path="/" element={<ProtectedRoute roles={["admin", "hr_manager"]}><DashboardPage /></ProtectedRoute>} />
              <Route path="/jobs" element={<ProtectedRoute roles={ALL_ROLES}><JobsPage /></ProtectedRoute>} />
              <Route path="/jobs/new" element={<ProtectedRoute roles={["admin", "hr_manager", "internal_hr"]}><JobFormPage /></ProtectedRoute>} />
              <Route path="/jobs/:id" element={<ProtectedRoute roles={ALL_ROLES}><JobDetailPage /></ProtectedRoute>} />
              <Route path="/jobs/:id/edit" element={<ProtectedRoute roles={["admin", "hr_manager", "internal_hr"]}><JobFormPage /></ProtectedRoute>} />
              <Route path="/candidates" element={<ProtectedRoute roles={ALL_ROLES}><CandidatesPage /></ProtectedRoute>} />
              <Route path="/candidates/:id" element={<ProtectedRoute roles={ALL_ROLES}><CandidateDetailPage /></ProtectedRoute>} />
              <Route path="/surveys" element={<ProtectedRoute roles={["admin", "hr_manager", "internal_hr", "internal_manager"]}><SurveysPage /></ProtectedRoute>} />
              <Route path="/scrapers" element={<ProtectedRoute roles={["admin"]}><ScrapersPage /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute roles={["admin", "hr_manager"]} developerOnly><UsersPage /></ProtectedRoute>} />
              <Route path="/shared-services" element={<ProtectedRoute roles={["admin", "hr_manager"]}><SharedServicesPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute roles={["admin"]}><SettingsPage /></ProtectedRoute>} />
              {/* Not in the navigation. See the import above. */}
              <Route path="/ci-shadow" element={<ProtectedRoute roles={["admin"]} developerOnly><CIShadowPage /></ProtectedRoute>} />
              {/* Open to every internal role since 2026-10-30 (Christian); external recruiters stay out — it lists every applicant's score. */}
              <Route path="/apt-shadow" element={<ProtectedRoute roles={["admin", "hr_manager", "internal_hr", "internal_manager"]}><AptShadowPage /></ProtectedRoute>} />
              {/* Same gate as /apt-shadow: every internal role, external
                  recruiters out. The rows carry names, emails and scores. */}
              <Route path="/recommendations" element={<ProtectedRoute roles={["admin", "hr_manager", "internal_hr", "internal_manager"]}><RecommendationsPage /></ProtectedRoute>} />
              <Route path="/dashboard/survey-completion" element={<ProtectedRoute roles={["admin", "hr_manager"]}><SurveyCompletionDashboardPage /></ProtectedRoute>} />
              <Route path="/stats" element={<ProtectedRoute roles={["admin", "hr_manager"]}><StatsIndexPage /></ProtectedRoute>} />
              <Route path="/stats/email-opens" element={<ProtectedRoute roles={["admin", "hr_manager"]}><EmailOpenRatesPage /></ProtectedRoute>} />
              <Route path="/stats/email-clicks" element={<ProtectedRoute roles={["admin", "hr_manager"]}><EmailClicksPage /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute roles={ALL_ROLES}><ReportsListPage /></ProtectedRoute>} />
              <Route path="/disqualify" element={<ProtectedRoute roles={["admin", "hr_manager", "internal_hr"]}><DisqualifyListPage /></ProtectedRoute>} />
              <Route path="/reports/new" element={<ProtectedRoute roles={ALL_ROLES}><ReportFormPage /></ProtectedRoute>} />
              <Route path="/reports/:id" element={<ProtectedRoute roles={ALL_ROLES}><ReportFormPage /></ProtectedRoute>} />
              <Route path="/reports/:id/view" element={<ProtectedRoute roles={ALL_ROLES}><ReportViewPage /></ProtectedRoute>} />
              <Route path="/pipeline" element={<ProtectedRoute roles={ALL_ROLES}><PipelinePage /></ProtectedRoute>} />
              <Route path="/team-metrics" element={<ProtectedRoute roles={["admin", "hr_manager"]}><TeamMetricsPage /></ProtectedRoute>} />
              {/* In-house CI project — developer-only, NOT in nav */}
              <Route path="/internal/ci-analysis" element={<ProtectedRoute roles={["admin", "hr_manager"]} developerOnly><CiLabPage /></ProtectedRoute>} />
              {/* Signable-documents admin (Sean's ask 2026-08-04).
                  Gated to `document_manager_user_ids` — currently
                  Christian + Sean only. Other admins get a 403 both
                  on the route and on every /api/documents/* endpoint. */}
              <Route path="/documents" element={<ProtectedRoute roles={["admin", "hr_manager"]} documentManagerOnly><DocumentsPage /></ProtectedRoute>} />
              <Route path="/documents/:documentId/signatures" element={<ProtectedRoute roles={["admin", "hr_manager"]} documentManagerOnly><SignaturesPage /></ProtectedRoute>} />
            </Routes>
          </Suspense>
          <ToastHost />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
